import React, {useEffect, useRef, useState} from 'react'
import moment from 'moment'
import ReactApexChart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'
import {getCSSVariableValue} from 'src/marketConnect/assets/ts/_utils'
import {useAppSelector} from 'src/redux/storeHooks'
import {GRAPH_DATA} from 'src/app/_interfaces/orderDetail.interface'
import DashboardDateFilter, {defaultDashboardDateRange} from './components/DashboardDateFilter'
import {PlatformDataType} from 'src/interfaces/Platform.interface'
import {DashboardOrderDateFilter, getOrderDateFilter} from './utils/dashboardPeriodUtils'
import {parseSafeNumber} from './utils/orderSummary'
import {formatDashboardCurrency, getCurrencySymbol} from './utils/parseDashboardReportResponse'
import Loader from 'src/utils/Loader'

type Props = {
  className?: string
  loading: boolean
  filters: {
    from_date: string
    to_date: string
    platform_id: string
    filter_type: 'quantity' | 'value' | ''
    date_filter?: DashboardOrderDateFilter
  }
  setFilters: React.Dispatch<
    React.SetStateAction<{
      from_date: string
      to_date: string
      platform_id: string
      filter_type: 'quantity' | 'value' | ''
      date_filter?: DashboardOrderDateFilter
    }>
  >
  graphData: GRAPH_DATA[]
  currencyIso: string
  currencySymbol?: string
}

const OrderReportChartNew: React.FC<Props> = ({
  className = '',
  filters,
  setFilters,
  graphData,
  loading = false,
  currencyIso,
  currencySymbol,
}) => {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = getCSSVariableValue('--bs-primary')
  const lightColor = getCSSVariableValue('--bs-primary-light')

  const chartRef = useRef<HTMLDivElement | null>(null)
  const [selectedPlatform, setSelectedPlatform] = useState(filters.platform_id || '')
  const [filterType, setFilterType] = useState<'quantity' | 'value' | ''>(filters.filter_type || 'value')
  const [selectedDate, setSelectedDate] = useState(defaultDashboardDateRange())

  const {fetchedConnectedPlatformData} = useAppSelector((state) => state.fetchConnectedPlatform)
  const {fetchedUserAccountData} = useAppSelector((state) => state.userAccountData)

  const [chartData, setChartData] = useState<number[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [maxYValue, setMaxYValue] = useState<number>(0)
  const [totalOrders, setOrders] = useState<number>(0)

  const destinationPlatforms = (fetchedConnectedPlatformData || []).filter(
    (channel: PlatformDataType) =>
      !(channel?.isMainStore || channel?.isAccountingStore || channel?.isShippingStore)
  )

  const isValueMode = filters.filter_type === 'value'
  const seriesLabel = isValueMode ? 'Order Value' : 'Orders'
  const totalLabel = isValueMode ? 'Total Value' : 'Total Orders'

  const formatDayLabel = (date: string) => moment(date).format('DD-MMM')

  useEffect(() => {
    const sanitizedGraphData = (graphData || []).filter((item) => item && item.orders)

    const data = sanitizedGraphData.map((item) => {
      const orders = item.orders as Record<string, any>
      if (isValueMode) {
        return parseSafeNumber(orders?.total_price ?? orders?.total_value ?? orders?.order_value ?? orders?.value ?? 0)
      }
      return parseSafeNumber(orders?.total_orders ?? orders?.orders ?? orders?.synced ?? 0)
    })

    const days = sanitizedGraphData
      .map((item) => item?.day)
      .filter(Boolean)
      .map((day) => formatDayLabel(day as string))

    setChartData(data)
    setCategories(days)
    setMaxYValue(data.length ? Math.max(...data) : 0)

    const total = data.reduce((sum, value) => sum + (Number.isFinite(value) ? value : 0), 0)
    setOrders(total)
  }, [graphData, isValueMode])

  const options: ApexOptions = {
    series: [{name: seriesLabel, data: chartData}],
    chart: {
      fontFamily: 'inherit',
      type: 'line',
      height: 300,
      toolbar: {show: false},
      zoom: {enabled: false},
    },
    markers: {
      colors: baseColor,
      size: 5,
      hover: {size: 8},
    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors: [baseColor],
    },
    xaxis: {
      categories,
      labels: {
        style: {colors: labelColor, fontSize: '12px'},
      },
    },
    yaxis: {
      min: 0,
      max: maxYValue + (isValueMode ? Math.ceil(maxYValue * 0.1) : 5),
      labels: {
        style: {colors: labelColor, fontSize: '12px'},
        formatter: (value) => (isValueMode ? formatDashboardCurrency(Math.ceil(value), currencyIso, 0, currencySymbol) : `${Math.ceil(value)}`),
      },
    },
    colors: [lightColor],
    grid: {
      borderColor,
      strokeDashArray: 2,
      yaxis: {lines: {show: true}},
    },
    tooltip: {
      y: {
        formatter: (value) =>
          isValueMode ? formatDashboardCurrency(value, currencyIso, 2, currencySymbol) : `${Math.round(value)} orders`,
      },
    },
  }

  const hasChartData = chartData.length > 0 && categories.length > 0
  const readOnly = fetchedUserAccountData?.role?.report_access === 'READ'

  const handleApply = () => {
    setFilters({
      from_date: selectedDate.from_date,
      to_date: selectedDate.to_date,
      platform_id: selectedPlatform,
      filter_type: filterType,
      date_filter: getOrderDateFilter(selectedDate.label),
    })
  }

  const handleReset = () => {
    const resetDate = defaultDashboardDateRange()
    setSelectedDate(resetDate)
    setSelectedPlatform('')
    setFilterType('value')
    setFilters({
      from_date: resetDate.from_date,
      to_date: resetDate.to_date,
      platform_id: '',
      filter_type: 'value',
      date_filter: 'LAST_30_DAYS',
    })
  }

  return (
    <div className={`dashboard-panel-card ${className}`}>
      <div className='dashboard-panel-header'>
        <h3>Order Report</h3>
        <div className='dashboard-filter-row'>
          <select
            value={filterType}
            className='dashboard-filter-select'
            onChange={(e) => setFilterType(e.target.value as 'quantity' | 'value' | '')}
            disabled={readOnly}
          >
            <option value='quantity'>By Quantity</option>
            <option value='value'>By Value ({currencySymbol || getCurrencySymbol(currencyIso)})</option>
          </select>

          <select
            value={selectedPlatform}
            className='dashboard-filter-select'
            style={{minWidth: '180px'}}
            onChange={(e) => setSelectedPlatform(e.target.value)}
            disabled={readOnly}
          >
            <option value=''>All Platforms</option>
            {destinationPlatforms.map((channel: PlatformDataType) => (
              <option key={channel.id} value={channel.id}>
                {channel.display_name}
              </option>
            ))}
          </select>

          <DashboardDateFilter
            value={selectedDate}
            onChange={setSelectedDate}
            onApply={handleApply}
            onReset={handleReset}
            disabled={readOnly}
            compact
          />
        </div>
      </div>

      <div className='dashboard-panel-body pb-2'>
        <div className='text-center mb-2'>
          <div className='fs-8 fw-semibold text-muted'>{totalLabel}</div>
          <div className='fs-2 fw-bold text-primary' style={{marginTop: '-4px'}}>
            {isValueMode ? formatDashboardCurrency(totalOrders, currencyIso, 2, currencySymbol) : totalOrders}
          </div>
        </div>

        {loading ? (
          <div className='d-flex justify-content-center py-10'>
            <Loader variant="section" />
          </div>
        ) : hasChartData ? (
          <div ref={chartRef} style={{height: '320px'}}>
            <ReactApexChart options={options} series={options.series} type='line' height={320} />
          </div>
        ) : (
          <div className='dashboard-empty'>No order data available yet.</div>
        )}
      </div>
    </div>
  )
}

export default OrderReportChartNew
